import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ProposalsPage() {
  const [proposals, setProposals] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const p = await api.get("/proposals");
        setProposals(p.data);
      } catch (e) { toast.error("Failed to load proposals"); }
    })();
  }, []);

  const deleteProposal = async (p) => {
    if (!window.confirm(`Delete "${p.client_legal_name}" (${p.status})? This can't be undone.`)) return;
    try {
      await api.delete(`/proposals/${p.id}`);
      toast.success("Proposal deleted");
      setProposals((prev) => prev.filter(x => x.id !== p.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = proposals.filter(p =>
    (!statusFilter || p.status === statusFilter) &&
    (!q || p.client_legal_name?.toLowerCase().includes(q.toLowerCase()) || p.proposal_type?.includes(q.toLowerCase()))
  );

  const STATUS_TABS = [
    { key: "", label: "All" },
    { key: "draft", label: "Draft" },
    { key: "pending_approval", label: "Pending" },
    { key: "approved", label: "Approved" },
    { key: "issued", label: "Issued" },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]" data-testid="proposals-page">
      <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="label-micro">Documents</div>
          <h1 className="font-display text-2xl sm:text-3xl font-black mt-1">Proposals</h1>
          <p className="text-sm text-slate-500 mt-1">Search, filter and open any proposal · {proposals.length} total</p>
        </div>
        <Link to="/proposals/new" data-testid="proposals-new-btn" className="inline-flex items-center gap-2 bg-slate-900 hover:bg-blue-700 text-white px-4 py-2.5 text-sm font-semibold rounded-sm transition-colors duration-200">
          <Plus size={16} weight="bold" /> New proposal
        </Link>
      </div>

      <div className="mt-6 bg-white border border-slate-200">
        <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-slate-200 flex-wrap">
          <div className="flex items-center gap-1 flex-wrap">
            {STATUS_TABS.map(t => (
              <button key={t.key || "all"} onClick={() => setStatusFilter(t.key)} data-testid={`proposals-tab-${t.key || "all"}`}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-sm border transition-colors ${statusFilter === t.key ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 hover:border-blue-500 text-slate-700"}`}>
                {t.label}
              </button>
            ))}
            <span className="text-xs text-slate-500 tabular-nums ml-2">{filtered.length} of {proposals.length}</span>
          </div>
          <input
            data-testid="proposals-search"
            placeholder="Search client or type…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="text-sm border border-slate-300 rounded-sm px-3 py-1.5 w-full sm:w-72 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="proposals-table">
          <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
            <tr className="text-left">
              <th className="px-4 sm:px-5 py-2.5 label-micro">Client</th>
              <th className="px-4 sm:px-5 py-2.5 label-micro">Type</th>
              <th className="px-4 sm:px-5 py-2.5 label-micro">Status</th>
              <th className="px-4 sm:px-5 py-2.5 label-micro">Prepared By</th>
              <th className="px-4 sm:px-5 py-2.5 label-micro">Updated</th>
              <th className="px-4 sm:px-5 py-2.5 label-micro w-16"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan="6" className="px-5 py-10 text-center text-slate-400 text-sm">
                {statusFilter ? <>No proposals with this status. <button onClick={() => setStatusFilter("")} className="text-blue-600 hover:underline">Clear filter</button></> : <>No proposals yet. Click <em>New proposal</em> to start.</>}
              </td></tr>
            )}
            {filtered.map((p) => (
              <tr key={p.id} onClick={() => nav(`/proposals/${p.id}`)} className="border-b border-slate-100 hover:bg-blue-50/50 cursor-pointer transition-colors duration-150" data-testid={`row-${p.id}`}>
                <td className="px-4 sm:px-5 py-3 font-semibold text-slate-900">{p.client_legal_name}</td>
                <td className="px-4 sm:px-5 py-3 capitalize text-slate-700">{p.proposal_type}</td>
                <td className="px-4 sm:px-5 py-3"><span className={`status-badge status-${p.status}`}>{p.status.replace('_',' ')}</span></td>
                <td className="px-4 sm:px-5 py-3 text-slate-600">{p.prepared_by_name}</td>
                <td className="px-4 sm:px-5 py-3 text-slate-500 tabular-nums text-xs">{new Date(p.updated_at).toLocaleString()}</td>
                <td className="px-4 sm:px-5 py-3">
                  <button onClick={(e) => { e.stopPropagation(); deleteProposal(p); }} data-testid={`row-delete-${p.id}`}
                          className="text-xs inline-flex items-center gap-1 border border-red-300 text-red-600 rounded-sm px-2 py-1 hover:bg-red-50 transition-colors" aria-label={`Delete ${p.client_legal_name}`}>
                    <X size={12} weight="bold"/>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
